import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, map, retry, switchMap, take } from 'rxjs/operators';

import { RefreshToken, Tokens } from '@ninety/platform/models';
import { AuthService } from '@ninety/platform/services/services/auth.service';
import { LogOutActions } from '@ninety/platform/state/app-global/auth/logout/logout.actions';

/**
 * Reference:
 * https://itnext.io/angular-tutorial-implement-refresh-token-with-httpinterceptor-bfa27b966f57
 */
let refreshInProgress = false;
const accessTokenB$ = new BehaviorSubject<string | null>(null);

export function RefreshTokenInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const store = inject(Store);

  return next(request).pipe(
    catchError((httpErr: HttpErrorResponse) => {
      // We don't want to refresh token for some requests like login or refresh
      if (
        request.url.toLowerCase().includes('signup') ||
        request.url.toLowerCase().includes('login') ||
        request.url.toLowerCase().includes('invite') ||
        request.url.toLowerCase().includes('refresh') ||
        request.url.toLowerCase().endsWith('/init')
      ) {
        return throwError(() => httpErr);
      }

      // Only send refresh on token expiration
      if (httpErr?.error?.errorMessage !== 'TokenExpiredError') {
        return throwError(() => httpErr);
      }

      // Wait until token is returned, then replay with new access token
      if (refreshInProgress) {
        return accessTokenB$.pipe(
          filter(val => val !== null),
          take(1),
          switchMap(token => {
            // If refresh fails, emit an empty token so queued requests can stop waiting.
            if (!token) {
              return throwError(() => httpErr);
            }
            return next(addAccessToken(request, token));
          })
        );
      }

      refreshInProgress = true;

      // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
      accessTokenB$.next(null);

      return authService.refreshAccessToken().pipe(
        // re-try refresh up to 3 times before forcing logout
        retry(3),
        catchError((pipeErr: unknown) => {
          // Unblock queued requests before logging out.
          accessTokenB$.next('');
          store.dispatch(LogOutActions.initiateLogOut());
          return throwError(() => pipeErr);
        }),
        map((resp: RefreshToken) => resp.tokens),
        filter(tokens => !!tokens?.access),
        switchMap((tokens: Tokens) => {
          accessTokenB$.next(tokens.access);

          return next(addAccessToken(request, tokens.access));
        }),
        // Always release the refresh lock, even when refresh fails.
        finalize(() => {
          refreshInProgress = false;
        }),
        // cloned request error doesn't matter for logout
        catchError((pipeErr: unknown) => throwError(() => pipeErr))
      );
    })
  );
}

function addAccessToken(request: HttpRequest<any>, accessToken: string) {
  if (!accessToken) {
    return request;
  }

  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}
